import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";
import Logo from "@/components/common/Logo";

const RUN_STEPS = [
  "Open /login",
  "Fill credentials",
  "Click “Sign in”",
  "Assert dashboard loads",
];

// Was three invented metrics ("10k+ tests run", "98% issues caught",
// "$0.004 avg / run") stated as fact on the sign-in and sign-up pages. These
// describe what the product DOES, which is true without measuring anything.
const STATS = [
  { value: "Plain English", label: "no scripts" },
  { value: "Real browser", label: "not a mock" },
  { value: "Per-run cost", label: "always shown" },
];

const POINTS = [
  "Describe tests in plain English",
  "Watch the plan run, step by step",
  "Auto bug reports with severity",
  "See the cost of every run",
];

// Animated marketing panel for the auth pages. Calm deep-navy theme with a
// looping "live run" card, animated stats, and a testimonial.
export default function AuthShowcase() {
  const [active, setActive] = useState(0);

  // Loop the mini run: each step completes, the result shows, then it resets.
  useEffect(() => {
    const id = setInterval(() => {
      setActive((a) => (a >= RUN_STEPS.length + 1 ? 0 : a + 1));
    }, 1100);
    return () => clearInterval(id);
  }, []);

  const finished = active > RUN_STEPS.length;

  return (
    <div className="relative z-10 flex w-full flex-col justify-between p-10 xl:p-12">
      {/* top: brand */}
      {/* The panel behind this stays dark in both themes, so the wordmark must
          too — the theme-aware default disappears here in light mode. */}
      <Link to="/" className="inline-flex">
        <Logo onDark />
      </Link>

      {/* middle: headline + live run card */}
      <div className="py-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-md font-display text-3xl font-bold leading-tight text-white xl:text-4xl"
        >
          QA automation that{" "}
          <span className="bg-gradient-to-r from-brand-300 to-fuchsia-300 bg-clip-text text-transparent">
            explains itself.
          </span>
        </motion.h2>

        {/* looping live-run card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-7 max-w-sm rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-semibold text-white">
              <Sparkles size={15} className="text-brand-300" />
              Live test run
            </span>
            <span className="flex items-center gap-1.5 text-[0.6875rem] font-medium text-emerald-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              running
            </span>
          </div>

          <ul className="mt-3 space-y-1.5">
            {RUN_STEPS.map((step, i) => {
              const done = i < active;
              const running = i === active && !finished;
              return (
                <li
                  key={step}
                  className="flex items-center gap-2.5 text-sm text-slate-300"
                >
                  {done ? (
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  ) : running ? (
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-brand-500/20 text-brand-200">
                      <Loader2 size={12} className="animate-spin" />
                    </span>
                  ) : (
                    <span className="h-5 w-5 rounded-full border-2 border-dashed border-white/20" />
                  )}
                  <span className={done ? "text-slate-200" : "text-slate-400"}>
                    {step}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 h-px bg-white/10" />
          <div className="mt-3 flex items-center justify-between">
            <motion.span
              key={finished ? "done" : "wait"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={
                finished
                  ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-300"
                  : "text-xs text-slate-400"
              }
            >
              {finished ? (
                <>
                  <Check size={12} /> PASSED
                </>
              ) : (
                "Validating outcome…"
              )}
            </motion.span>
            <span className="text-xs text-slate-400">⏱ 9s · $0.003</span>
          </div>
        </motion.div>

        {/* animated stats */}
        <div className="mt-7 grid max-w-sm grid-cols-3 gap-3">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.12 }}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 backdrop-blur-xl"
            >
              <div className="font-display text-lg font-bold text-white">
                {s.value}
              </div>
              <div className="text-[0.6875rem] text-slate-400">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* feature checklist */}
        <ul className="mt-7 space-y-2.5">
          {POINTS.map((p, i) => (
            <motion.li
              key={p}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className="flex items-center gap-3 text-sm text-slate-300"
            >
              <span className="grid h-5 w-5 place-items-center rounded-full bg-brand-500/20 text-brand-200">
                <Check size={12} strokeWidth={3} />
              </span>
              {p}
            </motion.li>
          ))}
        </ul>
      </div>

      {/* bottom: what a run costs you to read
          This was a fabricated testimonial — an invented quote attributed to an
          invented "Maya K. · QA Lead, Northwind" — presented as a real customer
          on the sign-in page. Replaced with something the product can back up. */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="flex max-w-sm items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-fuchsia-500 text-white">
          <Sparkles size={18} />
        </span>
        <div>
          <p className="text-sm text-slate-200">
            Every run keeps its plan, its screenshots and its exact token cost — so
            a failure is a report you can hand to a developer, not a rerun.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
